import clsx from "clsx";
import React from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import { getEnv, getSettings } from "src/services/configService";
import UserSettingsService from "src/services/userSettingsService";

import Divider from "@mui/material/Divider";
import MuiDrawer from "@mui/material/Drawer";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { SecondaryButton } from "@shared/CustomButton";
import { Logo } from "@shared/Logo/Logo";

import { useStyles } from "./Drawer.styles";
import { useWithVisibleNavigation } from "./Drawer.utils";
import { DrawerLogoLink } from "./DrawerLogoLink";
import { DrawerToogler } from "./DrawerToogler";

type DrawerContextType = { open: boolean; setOpen: React.Dispatch<React.SetStateAction<boolean>> };

const DrawerContext = React.createContext<DrawerContextType>({
    open: false,
    setOpen: () => null,
});

export function useDrawerContext() {
    const context = React.useContext(DrawerContext);
    if (!context) {
        throw new Error("useDrawerContext must be used within a DrawerContextProvider");
    }
    return context;
}

const mobileLinksContext: DrawerContextType = { open: true, setOpen: () => null };

type Props = React.PropsWithChildren<{ navigationContent: React.ReactNode }>;

export const Drawer = (props: Props) => {
    const [open, setOpen] = React.useState(UserSettingsService.getDrawerOpen());
    const [mobileOpen, setMobileOpen] = React.useState(false);

    const { classes } = useStyles();
    const { t } = useTranslation("common");
    const env = getEnv();
    const { features } = getSettings();
    const hasVisibleNavigation = useWithVisibleNavigation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const { pathname } = useLocation();
    const [menuPathname, setMenuPathname] = React.useState(pathname);

    if (pathname !== menuPathname) {
        setMenuPathname(pathname);
        setMobileOpen(false);
    }

    if (!hasVisibleNavigation) {
        return <main className={classes.content}>{props.children}</main>;
    }

    return (
        <DrawerContext.Provider value={isMobile ? mobileLinksContext : { open, setOpen }}>
            <div className={isMobile ? classes.mobileRoot : classes.root}>
                {isMobile ? (
                    <>
                        <div className={classes.mobileMenuHeaderContainer} data-testid="mobile-top-bar">
                            <DrawerLogoLink>
                                <Logo drawerOpen />
                            </DrawerLogoLink>
                            <SecondaryButton
                                onClick={() => setMobileOpen(true)}
                                tooltipProps={{ title: t("tooltip.openMenu") }}
                                data-testid="mobile-menu-open-button"
                            >
                                <i className="fa-solid fa-bars" />
                            </SecondaryButton>
                        </div>
                        <MuiDrawer
                            variant="temporary"
                            anchor="left"
                            open={mobileOpen}
                            onClose={() => setMobileOpen(false)}
                            classes={{ paper: classes.mobileDrawerPaper }}
                            ModalProps={{ keepMounted: true }}
                        >
                            <div className={classes.mobileMenuHeader}>
                                <DrawerLogoLink>
                                    <Logo drawerOpen />
                                </DrawerLogoLink>
                                <div className={classes.mobileMenuHeaderActions}>
                                    {features.environmentLabelEnabled ? (
                                        <div className={classes.mobileEnvLabel}>{env}</div>
                                    ) : null}
                                    <SecondaryButton
                                        onClick={() => setMobileOpen(false)}
                                        tooltipProps={{ title: t("tooltip.closeMenu") }}
                                        data-testid="mobile-menu-close-button"
                                    >
                                        <i className="fa-solid fa-xmark" />
                                    </SecondaryButton>
                                </div>
                            </div>
                            <Divider />
                            {props.navigationContent}
                        </MuiDrawer>
                    </>
                ) : (
                    <MuiDrawer
                        variant="permanent"
                        className={clsx(classes.drawer, {
                            [classes.drawerOpen]: open,
                            [classes.drawerClose]: !open,
                        })}
                        classes={{
                            paper: clsx({
                                [classes.drawerOpen]: open,
                                [classes.drawerClose]: !open,
                            }),
                        }}
                    >
                        <div className={classes.logoBox}>
                            <DrawerLogoLink>
                                <Logo drawerOpen={open} />
                            </DrawerLogoLink>
                            {features.environmentLabelEnabled && open ? <div className={classes.envLabel}>{env}</div> : null}
                            <DrawerToogler />
                        </div>
                        <Divider />
                        {props.navigationContent}
                    </MuiDrawer>
                )}
                <div className={classes.content}>
                    <main className={classes.main}>{props.children}</main>
                </div>
            </div>
        </DrawerContext.Provider>
    );
};
